import { ReactNode, RefObject, useRef, useState } from "react";
import { ThreeDotIcon } from "../../svg/three-dot-icon";
import AnchoredPortal from "../anchoredPortal/anchoredPortal";
import PopOverWithClickableOptions from "../popOverWithClickableOptions/popOverWithClickableOptions";

/**
 * Menu option configuration.
 */
export interface MenuOption {
  text: string;
  id: string;
}

/**
 * Position configuration for the dropdown menu.
 */
export interface MenuPositionConfig {
  topOffset?: number;
  rightOffset?: number;
  zIndex?: number;
}

/**
 * Props for the generic ThreeDotsMenu component.
 */
export interface ThreeDotsMenuProps {
  /** Array of menu options to display */
  options: MenuOption[];
  /** Callback when an option is clicked */
  onOptionClick: (optionId: string) => void;
  /** Optional callback when the menu is opened */
  onMenuOpen?: () => void;
  /** Optional children to render (e.g., modals) */
  children?: ReactNode;
  /** Optional custom icon component */
  icon?: ReactNode;
  /** Optional container class name */
  className?: string;
  /** Optional container id */
  id?: string;
  /** Optional position configuration */
  positionConfig?: MenuPositionConfig;
  /** Optional ref to external parent element for positioning */
  externalParentRef?: RefObject<HTMLDivElement>;
  /** Whether to disable the menu */
  disabled?: boolean;
}

const DEFAULT_POSITION_CONFIG: MenuPositionConfig = {
  topOffset: 5,
  rightOffset: 15,
  zIndex: 1000,
};

/**
 * A generic three-dots menu component that can be used throughout the application.
 *
 * @example
 * ```tsx
 * <ThreeDotsMenu
 *   options={[
 *     { text: "Edit", id: "edit" },
 *     { text: "Delete", id: "delete" },
 *   ]}
 *   onOptionClick={(id) => console.log("Clicked:", id)}
 *   onMenuOpen={() => console.log("Menu opened")}
 * />
 * ```
 */
const ThreeDotsMenu: React.FC<ThreeDotsMenuProps> = ({
  options,
  onOptionClick,
  onMenuOpen,
  children,
  icon,
  className = "three-dots-menu--container flex h-[22px] w-[22px] items-center justify-center bg-[#F7F9FA] hover:scale-105 hover:cursor-pointer",
  id,
  positionConfig = DEFAULT_POSITION_CONFIG,
  externalParentRef,
  disabled = false,
}) => {
  const internalRef = useRef<HTMLDivElement>(null);
  const parentRef = externalParentRef || internalRef;
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState({
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    width: 0,
    height: 0,
    x: 0,
    y: 0,
  });

  const handleMouseEnter = () => {
    if (disabled) return;
    // Calculate position only when menu is opened to avoid unnecessary re-renders
    if (parentRef.current) {
      const rect = parentRef.current.getBoundingClientRect();
      setPosition({
        top: rect.top,
        right: rect.right,
        bottom: rect.bottom,
        left: rect.left,
        width: rect.width,
        height: rect.height,
        x: rect.x,
        y: rect.y,
      });
    }
    setIsOpen(true);
    onMenuOpen?.();
  };

  const handleOptionClick = (optionId: string) => {
    onOptionClick(optionId);
  };

  const { topOffset, rightOffset, zIndex } = {
    ...DEFAULT_POSITION_CONFIG,
    ...positionConfig,
  };

  return (
    <div
      className={className}
      id={id}
      onMouseEnter={handleMouseEnter}
      ref={internalRef}
    >
      {icon || <ThreeDotIcon />}

      <AnchoredPortal
        parentRef={parentRef}
        shouldShow={isOpen && !disabled}
        top={position.bottom + (topOffset ?? 5)}
        right={rightOffset ?? 15}
        zIndex={zIndex ?? 1000}
      >
        <div onMouseLeave={() => setIsOpen(false)}>
          <PopOverWithClickableOptions
            options={options}
            onClick={handleOptionClick}
          />
        </div>
      </AnchoredPortal>

      {children}
    </div>
  );
};

export default ThreeDotsMenu;
